import { useState, useEffect } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, Cell,
} from "recharts";
import { HelpCircle } from "lucide-react";

const mono = { fontFamily: "'JetBrains Mono', 'Courier New', monospace" };

const MONEDAS = [
  { code: "CLP", symbol: "$",   locale: "es-CL", flag: "🇨🇱", name: "Peso chileno"    },
  { code: "ARS", symbol: "$",   locale: "es-AR", flag: "🇦🇷", name: "Peso argentino"  },
  { code: "COP", symbol: "$",   locale: "es-CO", flag: "🇨🇴", name: "Peso colombiano" },
  { code: "MXN", symbol: "$",   locale: "es-MX", flag: "🇲🇽", name: "Peso mexicano"   },
  { code: "PEN", symbol: "S/",  locale: "es-PE", flag: "🇵🇪", name: "Sol peruano"     },
  { code: "BRL", symbol: "R$",  locale: "pt-BR", flag: "🇧🇷", name: "Real brasileño"  },
  { code: "UYU", symbol: "$",   locale: "es-UY", flag: "🇺🇾", name: "Peso uruguayo"   },
  { code: "USD", symbol: "US$", locale: "en-US", flag: "🇺🇸", name: "Dólar (USD)"     },
];

const fmtMoney = (n, symbol, locale) =>
  symbol + Math.round(n ?? 0).toLocaleString(locale);

// ── Calcular sugerencias desde resumen real ───────────────────────────────────
function calcularSugerencias(resumen) {
  const entregados = resumen?.total_entregados ?? 0;
  const confirmados = resumen?.total_confirmados ?? 0;

  const te   = resumen?.tasa_entrega      ?? 70;
  const tc   = resumen?.tasa_confirmacion ?? 70;
  const ce   = entregados > 0
    ? Math.round((resumen?.flete_entregados_total  ?? 0) / entregados)
    : 7500;
  const cff  = entregados > 0
    ? Math.round((resumen?.flete_devoluciones_total ?? 0) / entregados)
    : 0;
  const cc   = 5000;

  return { te, tc, ce, cff, cc, tieneDatos: entregados > 0 };
}

// ── Input con chip de sugerencia ─────────────────────────────────────────────
function Field({ label, hint, tooltip, value, onChange, suffix, sugerido, sugeridoLabel, onAplicar, fmt }) {
  return (
    <div>
      <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 4 }}>
        <span>{label}</span>
        {tooltip && (
          <span title={tooltip} style={{ cursor: "help", color: "#9496B4", display: "inline-flex" }}>
            <HelpCircle size={13} strokeWidth={2} />
          </span>
        )}
      </label>
      <div style={{ position: "relative" }}>
        <input
          type="number"
          min="0"
          value={value}
          onChange={e => onChange(e.target.value)}
          style={{
            width: "100%",
            padding: suffix ? "9px 44px 9px 12px" : "9px 12px",
            border: "1px solid #E4E6F5",
            borderRadius: 8,
            fontSize: 14,
            color: "#1E1E2E",
            background: "#fff",
            outline: "none",
            boxSizing: "border-box",
            ...mono,
          }}
        />
        {suffix && (
          <span style={{
            position: "absolute", right: 10, top: "50%",
            transform: "translateY(-50%)", fontSize: 11, color: "#9496B4",
          }}>
            {suffix}
          </span>
        )}
      </div>
      {hint && (
        <div style={{ fontSize: 11, color: "#B0B2CC", marginTop: 3 }}>{hint}</div>
      )}
      {sugerido != null && (
        <div style={{
          marginTop: 5, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap",
        }}>
          <button
            onClick={() => onAplicar(sugerido)}
            style={{
              fontSize: 11, padding: "2px 8px", borderRadius: 12,
              border: "1px solid #059669", background: "#ECFDF5",
              color: "#059669", cursor: "pointer", fontWeight: 600,
              whiteSpace: "nowrap",
            }}
          >
            Usar {typeof sugerido === "number" && suffix === "%" ? sugerido.toFixed(1) + "%" : (fmt ? fmt(sugerido) : sugerido)} ↑
          </button>
          <span style={{ fontSize: 11, color: "#6B7280" }}>{sugeridoLabel}</span>
        </div>
      )}
    </div>
  );
}

// ── Campo de Precio de Venta — grande y destacado ────────────────────────────
function CampoPV({ value, onChange, symbol, code }) {
  return (
    <div style={{
      background: "#F0FDF4",
      border: "2px solid #059669",
      borderRadius: 14, padding: "20px 24px",
    }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: "#059669", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>
        💰 Tu precio de venta al público (PV)
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <span style={{ fontSize: 28, fontWeight: 700, color: "#059669" }}>{symbol}</span>
        <input
          type="number"
          min="0"
          value={value}
          onChange={e => onChange(e.target.value)}
          style={{
            flex: 1, background: "transparent", border: "none", outline: "none",
            fontSize: 36, fontWeight: 800, color: "#111827", ...mono,
            minWidth: 0,
          }}
        />
        <span style={{ fontSize: 14, color: "#059669", fontWeight: 600, alignSelf: "flex-end", marginBottom: 6 }}>{code}</span>
      </div>
      <div style={{ fontSize: 12, color: "#374151", marginTop: 6 }}>
        Ingresá el precio al que ofrecés el producto. El cálculo te dice si ese precio cubre todos tus costos reales.
      </div>
    </div>
  );
}

// ── Desglose visual de cómo se arma el PM ────────────────────────────────────
function FormulaDesglose({ CU, CFF, CE, CC, TC, TE, PM, fmt }) {
  const te_d = Math.max(TE / 100, 0.001);
  const tc_d = Math.max(TC / 100, 0.001);

  const aporteEnvio = CE / te_d;
  const aporteMeta  = CC / (tc_d * te_d);

  const rows = [
    {
      icon: "💼", label: "Costo del producto",
      sub: "Lo que pagás al proveedor por unidad",
      value: CU,
    },
    {
      icon: "📦", label: "Fletes fallidos absorbidos",
      sub: `Costo de retornos repartido en cada pedido entregado`,
      value: CFF,
    },
    {
      icon: "🚚", label: "Envío — diluido por tasa de entrega",
      sub: `${fmt(CE)} ÷ ${TE.toFixed(0)}% TE = lo que pagás de flete por cada entregado`,
      value: aporteEnvio,
      divisor: true,
    },
    {
      icon: "📣", label: "Meta Ads — diluido por confirmación y entrega",
      sub: `${fmt(CC)} ÷ (${TC.toFixed(0)}% TC × ${TE.toFixed(0)}% TE) = publicidad por cada entregado`,
      value: aporteMeta,
      divisor: true,
    },
  ];

  return (
    <div style={{
      background: "#F9FAFB", border: "1px solid #E5E7EB",
      borderRadius: 12, padding: "16px 20px", marginBottom: "1.5rem",
    }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 14 }}>
        📐 Así se arma el Precio Mínimo
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
        {rows.map((r, i) => (
          <div key={i} style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "9px 0",
            borderBottom: i < rows.length - 1 ? "1px dashed #E5E7EB" : "none",
          }}>
            <div style={{ flex: 1, minWidth: 0, paddingRight: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: "#374151" }}>
                {r.icon} {r.label}
              </div>
              <div style={{ fontSize: 11, color: "#9496B4", marginTop: 2 }}>{r.sub}</div>
            </div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#374151", ...mono, flexShrink: 0 }}>
              + {fmt(r.value)}
            </div>
          </div>
        ))}

        {/* Total */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          borderTop: "2px solid #111827", marginTop: 8, paddingTop: 10,
        }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>
            🎯 Precio Mínimo (PM) — suma de todo lo anterior
          </div>
          <div style={{ fontSize: 20, fontWeight: 800, color: "#111827", ...mono }}>
            {fmt(PM)}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Card de resultado ────────────────────────────────────────────────────────
function ResultCard({ label, value, desc, color, span }) {
  return (
    <div style={{
      background: "#fff", border: "1px solid #E4E6F5",
      borderRadius: 12, padding: "16px 20px",
      gridColumn: span ? "1 / -1" : undefined,
    }}>
      <div style={{ fontSize: 11, color: "#9496B4", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 500, marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ fontSize: 26, fontWeight: 700, color: color ?? "#1E1E2E", ...mono }}>
        {value}
      </div>
      {desc && <div style={{ fontSize: 12, color: "#9496B4", marginTop: 4 }}>{desc}</div>}
    </div>
  );
}

// ── Sección titular ──────────────────────────────────────────────────────────
function SectionTitle({ title, sub }) {
  return (
    <div style={{ marginBottom: "1.25rem" }}>
      <div style={{ fontSize: 16, fontWeight: 700, color: "#1E1E2E" }}>{title}</div>
      {sub && <div style={{ fontSize: 12, color: "#9496B4", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

// ── Componente principal ─────────────────────────────────────────────────────
export default function Calculadora({ resumen, modoPro }) {
  const sug = calcularSugerencias(resumen);

  const [monedaCodigo, setMonedaCodigo] = useState("CLP");
  const moneda = MONEDAS.find(m => m.code === monedaCodigo) || MONEDAS[0];
  const fmt = n => fmtMoney(n, moneda.symbol, moneda.locale);

  // Tasas de cambio relativas a CLP (1 CLP = X moneda)
  const [rates,       setRates]       = useState({ CLP: 1 });
  const [ratesStatus, setRatesStatus] = useState("cargando"); // "cargando" | "ok" | "error"

  useEffect(() => {
    fetch("https://open.er-api.com/v6/latest/CLP")
      .then(r => r.json())
      .then(data => {
        if (data?.rates) {
          setRates({ CLP: 1, ...data.rates });
          setRatesStatus("ok");
        } else {
          setRatesStatus("error");
        }
      })
      .catch(() => setRatesStatus("error"));
  }, []);

  const [cu,  setCu]  = useState(3500);
  const [ce,  setCe]  = useState(sug.ce);
  const [cff, setCff] = useState(sug.cff);
  const [tc,  setTc]  = useState(sug.tc);
  const [te,  setTe]  = useState(sug.te);
  const [pv,  setPv]  = useState(25000);
  const [cc,  setCc]  = useState(sug.cc);

  // Cambio de moneda con conversión de valores
  const handleMonedaChange = (newCode) => {
    if (newCode === monedaCodigo) return;
    const fromRate = rates[monedaCodigo] ?? 1;
    const toRate   = rates[newCode]      ?? 1;
    const factor   = toRate / fromRate;
    const conv     = v => String(Math.round((parseFloat(v) || 0) * factor));
    setCu(conv(cu)); setCe(conv(ce)); setCff(conv(cff));
    setCc(conv(cc)); setPv(conv(pv));
    setMonedaCodigo(newCode);
  };

  // Actualizar sugerencias cuando llegan los datos reales
  useEffect(() => {
    const s = calcularSugerencias(resumen);
    setCe(s.ce);
    setCff(s.cff);
    setTc(s.tc);
    setTe(s.te);
  }, [resumen]);

  // Conversiones seguras
  const CU  = parseFloat(cu)  || 0;
  const CE  = parseFloat(ce)  || 0;
  const CFF = parseFloat(cff) || 0;
  const TC  = Math.max(parseFloat(tc)  || 0.01, 0.01);
  const TE  = Math.max(parseFloat(te)  || 0.01, 0.01);
  const PV  = parseFloat(pv)  || 0;
  const CC  = parseFloat(cc)  || 0;

  const tc_d = TC / 100;
  const te_d = TE / 100;

  // Costo base = producto + fletes fallidos absorbidos
  const COSTO_BASE = CU + CFF;

  // PM = Costo base + (flete entregados / TE) + (Meta / (TC × TE))
  const PM     = COSTO_BASE + (CE / te_d) + (CC / (tc_d * te_d));
  const BEROAS = PV > 0 ? PM / PV : 0;
  const CPA_BE = PV > 0 ? (PV - PM) / (tc_d * te_d) : 0;
  const MARGEN = PV - COSTO_BASE - (CE / te_d);

  let alertaColor, alertaLabel;
  if (PV > PM * 1.2) {
    alertaColor = "#059669"; alertaLabel = "✅ Precio saludable — hay margen real";
  } else if (PV > PM) {
    alertaColor = "#D97706"; alertaLabel = "⚠️ Precio ajustado — margen muy pequeño";
  } else {
    alertaColor = "#DC2626"; alertaLabel = "🚨 Precio insuficiente — estás perdiendo dinero";
  }

  // Sección 2: BEROAS en tiempo real
  const tc_real = (resumen?.tasa_confirmacion ?? 0) / 100;
  const te_real = (resumen?.tasa_entrega      ?? 0) / 100;
  const PM_real = tc_real > 0 && te_real > 0
    ? COSTO_BASE + (CE / te_real) + (CC / (tc_real * te_real))
    : null;
  const BEROAS_real = PM_real != null && PV > 0 ? PM_real / PV : null;
  const ROAS_real   = resumen?.gasto_meta_total > 0
    ? (resumen?.ingresos_netos_total ?? 0) / resumen.gasto_meta_total
    : null;
  const diff = BEROAS_real != null && ROAS_real != null ? ROAS_real - BEROAS_real : null;
  const chartData = BEROAS_real != null && ROAS_real != null
    ? [
        { name: "BEROAS",    value: parseFloat(BEROAS_real.toFixed(2)), fill: "#9496B4" },
        { name: "ROAS Real", value: parseFloat(ROAS_real.toFixed(2)),   fill: diff >= 0 ? "#059669" : "#DC2626" },
      ]
    : [];

  return (
    <div>

      {/* ════════════════════════════════════════════════════════════════════
          SECCIÓN 1: CALCULADORA DE PRECIO MÍNIMO
          ════════════════════════════════════════════════════════════════════ */}
      <div style={{
        background: "#fff", border: "1px solid #E4E6F5",
        borderRadius: 12, padding: "1.5rem", marginBottom: "1.25rem",
      }}>
        {/* Selector de moneda */}
        <div style={{ marginBottom: "1.5rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 8 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: "#6B7280" }}>Moneda:</span>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {MONEDAS.map(m => {
                const activa = m.code === monedaCodigo;
                return (
                  <button
                    key={m.code}
                    onClick={() => handleMonedaChange(m.code)}
                    title={m.name}
                    disabled={ratesStatus === "cargando"}
                    style={{
                      fontSize: 12, fontWeight: activa ? 700 : 400,
                      padding: "4px 12px", borderRadius: 20, cursor: ratesStatus === "cargando" ? "wait" : "pointer",
                      border: `1.5px solid ${activa ? "#059669" : "#E5E7EB"}`,
                      background: activa ? "#ECFDF5" : "#fff",
                      color: activa ? "#059669" : "#6B7280",
                      transition: "all 0.15s", opacity: ratesStatus === "cargando" ? 0.6 : 1,
                    }}
                  >
                    {m.flag} {m.code}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Indicador de tipo de cambio */}
          <div style={{ fontSize: 11, color: "#9496B4", display: "flex", alignItems: "center", gap: 6 }}>
            {ratesStatus === "cargando" && <span>⏳ Cargando tipos de cambio...</span>}
            {ratesStatus === "error"    && <span style={{ color: "#D97706" }}>⚠️ Sin conexión a tasas — los valores no se convierten automáticamente</span>}
            {ratesStatus === "ok" && monedaCodigo !== "CLP" && (
              <span>
                Tipo de cambio: 1 CLP = {fmtMoney(rates[monedaCodigo] ?? 1, moneda.symbol, moneda.locale)} · Fuente: open.er-api.com
              </span>
            )}
            {ratesStatus === "ok" && monedaCodigo === "CLP" && (
              <span>{moneda.name} · Tasas actualizadas ✓</span>
            )}
          </div>
        </div>

        <SectionTitle
          title="Calculadora de Precio Mínimo"
          sub="Completá tus costos y tasas — los valores sugeridos se calculan automáticamente de tus pedidos cargados"
        />

        {/* ── Fila 1: Inputs de costos y tasas ── */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(clamp(130px, 44%, 200px), 1fr))", gap: "clamp(0.75rem, 2vw, 1rem)", marginBottom: "1.5rem" }}>

          <Field
            label="💼 Costo Unitario (CU)"
            tooltip="CU = Costo Unitario. El precio que pagas al proveedor por cada unidad del producto, sin contar envío ni fees."
            hint="Lo que pagás al proveedor por cada unidad"
            value={cu} onChange={setCu} suffix={moneda.code} fmt={fmt}
          />

          <Field
            label="🚚 Costo de Envío (CE)"
            tooltip="CE = Costo de Envío. El flete promedio que pagas por cada pedido que efectivamente fue entregado. No incluye fletes fallidos."
            hint="Flete promedio de pedidos entregados"
            value={ce} onChange={setCe} suffix={moneda.code}
            sugerido={sug.tieneDatos ? sug.ce : 7500}
            sugeridoLabel={sug.tieneDatos ? "📊 Promedio real de tus fletes entregados" : "💡 Valor sugerido por defecto"}
            onAplicar={v => setCe(String(Math.round(v)))} fmt={fmt}
          />

          <Field
            label="📦 Fletes Fallidos (CFF)"
            tooltip="CFF = Costo de Fletes Fallidos. Lo que pagas en flete por pedidos devueltos o no entregados, prorrateado sobre cada pedido entregado. La pérdida invisible que ninguna calculadora estándar incluye."
            hint="Costo de retornos por cada pedido entregado"
            value={cff} onChange={setCff} suffix={moneda.code}
            sugerido={sug.tieneDatos ? sug.cff : null}
            sugeridoLabel={sug.tieneDatos ? "📊 Total fallidos ÷ entregados (costo real absorbido)" : null}
            onAplicar={v => setCff(String(Math.round(v)))} fmt={fmt}
          />

          <Field
            label="✅ Tasa de Confirmación % (TC)"
            tooltip="TC = Tasa de Confirmación. De cada 100 leads captados por Meta, cuántos confirman el pedido por teléfono. Sano sobre 75%."
            hint="De cada 100 captados, ¿cuántos confirman?"
            value={tc} onChange={setTc} suffix="%"
            sugerido={sug.tieneDatos ? parseFloat(sug.tc.toFixed(1)) : 70}
            sugeridoLabel={sug.tieneDatos ? "📊 Promedio real de tus pedidos" : "💡 Valor sugerido por defecto"}
            onAplicar={v => setTc(String(v))}
          />

          <Field
            label="📬 Tasa de Entrega % (TE)"
            tooltip="TE = Tasa de Entrega. De cada 100 pedidos confirmados, cuántos efectivamente reciben el producto y pagan. Sano sobre 65%."
            hint="De cada 100 confirmados, ¿cuántos llegan?"
            value={te} onChange={setTe} suffix="%"
            sugerido={sug.tieneDatos ? parseFloat(sug.te.toFixed(1)) : 70}
            sugeridoLabel={sug.tieneDatos ? "📊 Promedio real de tus pedidos" : "💡 Valor sugerido por defecto"}
            onAplicar={v => setTe(String(v))}
          />

          <Field
            label="📣 Gasto en Meta Ads (CC)"
            tooltip="CC = Costo de Captación. Lo que gastas en pauta de Meta por cada pedido captado (no por cada pagado). Si Meta te dice que el costo por resultado es $5 USD, ese es tu CC."
            hint="¿Cuánto invertís en publicidad por pedido captado?"
            value={cc} onChange={setCc} suffix={moneda.code}
            sugerido={5000}
            sugeridoLabel="💡 Punto de partida sugerido para escalar"
            onAplicar={v => setCc(String(v))} fmt={fmt}
          />

        </div>

        {/* ── Fila 2: PM y PV en la misma fila, mismo tamaño ── */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(clamp(200px, 45%, 500px), 1fr))", gap: "clamp(0.75rem, 2vw, 1rem)", marginBottom: "1.25rem" }}>

          {/* PM — precio mínimo */}
          <div style={{
            border: `2px solid ${PV > PM ? "#059669" : "#DC2626"}`,
            borderRadius: 14, padding: "20px 24px",
            background: PV > PM ? "#F0FDF4" : "#FFF1F2",
            display: "flex", flexDirection: "column", justifyContent: "center",
          }}>
            <div style={{ fontSize: 11, color: "#9496B4", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600, marginBottom: 8 }}>
              🎯 Precio Mínimo para no perder (PM)
            </div>
            <div style={{ fontSize: 36, fontWeight: 800, color: PV > PM ? "#059669" : "#DC2626", ...mono, lineHeight: 1 }}>
              {fmt(PM)}
            </div>
            <div style={{ fontSize: 11, color: "#9496B4", marginTop: 8, lineHeight: 1.5 }}>
              Costo + Envío ÷ TE + Meta ÷ (TC × TE)
            </div>
          </div>

          {/* PV — precio de venta del usuario */}
          <CampoPV value={pv} onChange={setPv} symbol={moneda.symbol} code={moneda.code} />

        </div>

        {/* Badge viabilidad */}
        <div style={{ marginBottom: "1.5rem" }}>
          <span style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            padding: "6px 16px", borderRadius: 20,
            background: alertaColor + "18", color: alertaColor,
            fontWeight: 600, fontSize: 13, border: `1px solid ${alertaColor}40`,
          }}>
            {alertaLabel}
          </span>
        </div>

        {/* Desglose de fórmula */}
        <FormulaDesglose CU={CU} CFF={CFF} CE={CE} CC={CC} TC={TC} TE={TE} PM={PM} fmt={fmt} />

        {/* Resultados secundarios */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(clamp(140px, 45%, 200px), 1fr))", gap: "clamp(0.75rem, 2vw, 1rem)" }}>

          {/* BEROAS */}
          <ResultCard
            label="BEROAS — ROAS mínimo para no perder"
            value={BEROAS.toFixed(2) + "x"}
            desc="Si tu ROAS real está por debajo de este número, Meta te está comiendo el margen"
            color="#4338CA"
          />

          {/* Margen sin ads */}
          <ResultCard
            label="📈 Margen por entregado (sin Meta)"
            value={fmt(MARGEN)}
            desc="Lo que te queda por pedido entregado antes de pagar publicidad"
            color={MARGEN >= 0 ? "#059669" : "#DC2626"}
          />

          {/* CPA Break Even */}
          <div style={{
            gridColumn: "1 / -1",
            background: CPA_BE >= 0 ? "#F0FDF4" : "#FEF2F2",
            border: `1px solid ${CPA_BE >= 0 ? "#059669" : "#DC2626"}30`,
            borderRadius: 12, padding: "16px 20px",
          }}>
            <div style={{ fontSize: 11, color: "#9496B4", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 500, marginBottom: 6 }}>
              💸 CPA Máximo — lo más que podés pagar por un pedido captado
            </div>
            <div style={{ fontSize: 26, fontWeight: 700, color: CPA_BE >= 0 ? "#059669" : "#DC2626", ...mono }}>
              {CPA_BE >= 0 ? fmt(CPA_BE) : "Sin margen"}
            </div>
            <div style={{ fontSize: 12, color: CPA_BE >= 0 ? "#059669" : "#DC2626", marginTop: 4, fontWeight: 500 }}>
              {CPA_BE >= 0
                ? `Si Meta te cobra más de ${fmt(CPA_BE)} por pedido captado, estás perdiendo plata`
                : "Con este precio no hay margen para pagar publicidad"}
            </div>
          </div>

        </div>
      </div>

      {/* ════════════════════════════════════════════════════════════════════
          SECCIÓN 2: BEROAS EN TIEMPO REAL
          ════════════════════════════════════════════════════════════════════ */}
      {!modoPro ? (
        <div style={{
          background: "#fff", border: "1px solid #E4E6F5",
          borderRadius: 12, padding: "2rem", textAlign: "center",
        }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>📊</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: "#1E1E2E", marginBottom: 8 }}>
            BEROAS en Tiempo Real
          </div>
          <div style={{ fontSize: 13, color: "#9496B4", marginBottom: 20, maxWidth: 400, margin: "0 auto 20px" }}>
            Compará tu BEROAS con el ROAS real usando las tasas reales de tus archivos cargados. Disponible en el plan Pro.
          </div>
          <span style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            padding: "8px 20px", borderRadius: 20,
            background: "#EEF2FF", color: "#4338CA",
            fontWeight: 600, fontSize: 13, border: "1px solid #818CF8",
          }}>
            🔒 Requiere plan Pro
          </span>
        </div>
      ) : (
        <div style={{
          background: "#fff", border: "1px solid #E4E6F5",
          borderRadius: 12, padding: "1.5rem",
        }}>
          <SectionTitle
            title="BEROAS en Tiempo Real"
            sub="Compara tu BEROAS calculado con el ROAS real del período usando los datos de tus archivos"
          />

          {!resumen || !(resumen.gasto_meta_total > 0) ? (
            <div style={{
              textAlign: "center", padding: "2.5rem 1rem",
              color: "#9496B4", fontSize: 14,
              background: "#F7F7FC", borderRadius: 10,
            }}>
              Sin datos del período cargado. Cargá un archivo de ventas para ver esta sección.
            </div>
          ) : (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(clamp(130px, 44%, 200px), 1fr))", gap: "clamp(0.75rem, 2vw, 1rem)", marginBottom: "1.25rem" }}>
                {[
                  { label: "TC real del período",  value: (resumen.tasa_confirmacion ?? 0).toFixed(1) + "%", color: "#4338CA" },
                  { label: "TE real del período",  value: (resumen.tasa_entrega      ?? 0).toFixed(1) + "%", color: "#4338CA" },
                  { label: "Gasto Meta Ads",        value: fmt(resumen.gasto_meta_total),                    color: "#1E1E2E" },
                ].map(k => (
                  <div key={k.label} style={{ background: "#F7F7FC", borderRadius: 10, padding: "14px 16px" }}>
                    <div style={{ fontSize: 11, color: "#9496B4", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 500, marginBottom: 5 }}>
                      {k.label}
                    </div>
                    <div style={{ fontSize: 20, fontWeight: 600, color: k.color, ...mono }}>
                      {k.value}
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(clamp(140px, 45%, 200px), 1fr))", gap: "clamp(0.75rem, 2vw, 1rem)", marginBottom: "1.25rem" }}>
                <div style={{ background: "#fff", border: "1px solid #E4E6F5", borderRadius: 12, padding: "16px 20px" }}>
                  <div style={{ fontSize: 11, color: "#9496B4", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 500, marginBottom: 5 }}>
                    BEROAS (tasas reales del período)
                  </div>
                  <div style={{ fontSize: 28, fontWeight: 700, color: "#4338CA", ...mono }}>
                    {BEROAS_real != null ? BEROAS_real.toFixed(2) + "x" : "—"}
                  </div>
                  <div style={{ fontSize: 12, color: "#9496B4", marginTop: 4 }}>
                    PM recalculado con TC/TE reales
                  </div>
                </div>

                <div style={{ background: "#fff", border: "1px solid #E4E6F5", borderRadius: 12, padding: "16px 20px" }}>
                  <div style={{ fontSize: 11, color: "#9496B4", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 500, marginBottom: 5 }}>
                    ROAS Real del período
                  </div>
                  <div style={{
                    fontSize: 28, fontWeight: 700, ...mono,
                    color: ROAS_real != null && BEROAS_real != null && ROAS_real >= BEROAS_real ? "#059669" : "#DC2626",
                  }}>
                    {ROAS_real != null ? ROAS_real.toFixed(2) + "x" : "—"}
                  </div>
                  <div style={{ fontSize: 12, color: "#9496B4", marginTop: 4 }}>
                    Ingresos netos ÷ Gasto Meta Ads
                  </div>
                </div>
              </div>

              {diff != null && (
                <div style={{
                  background: diff >= 0 ? "#F0FDF4" : "#FEF2F2",
                  border: `1px solid ${diff >= 0 ? "#059669" : "#DC2626"}40`,
                  borderRadius: 12, padding: "14px 20px", marginBottom: "1.25rem",
                  display: "flex", alignItems: "center", gap: 16,
                }}>
                  <div style={{ fontSize: 28, fontWeight: 700, color: diff >= 0 ? "#059669" : "#DC2626", ...mono }}>
                    {diff >= 0 ? "+" : ""}{diff.toFixed(2)}x
                  </div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: diff >= 0 ? "#059669" : "#DC2626" }}>
                      {diff >= 0 ? "✅ Tu campaña está generando ganancias reales" : "🚨 Tu campaña está destruyendo margen"}
                    </div>
                    <div style={{ fontSize: 12, color: "#9496B4", marginTop: 2 }}>
                      ROAS Real − BEROAS = diferencia
                    </div>
                  </div>
                </div>
              )}

              {chartData.length > 0 && (
                <div>
                  <div style={{ fontSize: 11, color: "#9496B4", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 500, marginBottom: "0.75rem" }}>
                    BEROAS vs ROAS Real
                  </div>
                  <ResponsiveContainer width="100%" height={110}>
                    <BarChart data={chartData} layout="vertical" margin={{ top: 0, right: 60, left: 10, bottom: 0 }}>
                      <XAxis type="number" tick={{ fontSize: 10, fill: "#9496B4" }} tickFormatter={v => v + "x"} axisLine={false} tickLine={false} />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 12, fill: "#4B4D6E", fontWeight: 500 }} width={95} axisLine={false} tickLine={false} />
                      <Tooltip formatter={v => [v + "x", ""]} contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #E4E6F5" }} />
                      <Bar dataKey="value" radius={[0, 5, 5, 0]} barSize={32}>
                        {chartData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
